import { checkHost } from "./checkHost";
checkHost();

import "./app.scss";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWindowResize } from "@hooks/index";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useShowAccountCrowdModal } from "@layouts/GlobalAccountCrowd/hooks";
import report from "@report";
import { fetchGlobalConfig } from "@store/configurations/configSlice";
import {
  fetchAiCredits,
  updateCreditInfo,
  updateIsScreenlt720,
  updatePageLoading,
  updateUserInfo,
} from "@store/globalSlice";
import SpriteUrl from "@components/SvgIcon/sprite.svg?react";
import { LoginRouter } from "../router";
import { DEFAULT_PAGE } from "../routes";
import { useInitApp } from "./hooks";
import i18next from "i18next";
import GlobalLayout from "./GlobalLayout";
import classNames from "classnames";
import { isNewLoginConfig } from "@pages/home/components/NewRightContent/config";
import { getProfile } from "@apis/http/api";

let startPageTime = new Date().getTime();

const RenderPage = () => {
  const { i18Ready, pageLoading } = useAppSelector((state) => state.globalReducer);

  const { ready: configReady } = useAppSelector((state) => state.configReducer);

  const pageReady = !pageLoading && i18Ready && configReady;

  useEffect(() => {
    if (pageReady === true) {
      report.event("kalo_pageready", { kalo_value: performance.now() });
    }
  }, [pageReady]);

  useEffect(() => {
    i18next.setDefaultNamespace("tiktok");
  }, []);

  return <></>;
};

const App = () => {
  const location = useLocation();
  const { pageLoading, language } = useAppSelector((state) => state.globalReducer);
  const { ready: configReady } = useAppSelector((state) => state.configReducer);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setVisibleRemainderCountModal } = useShowAccountCrowdModal();
  useInitApp();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    // 用户信息接口完成后 上报一次
    if (!pageLoading) {
      startPageTime = new Date().getTime();
    }
  }, [location.pathname, pageLoading]);

  useEffect(() => {
    getProfile()
      .then((res) => {
        setTimeout(() => {
          dispatch(updatePageLoading(false));
        }, 30);
        if (res && res.username) {
          dispatch(updateUserInfo(res));
          if (["/login", "/signup", "/authorization"].includes(window.location.pathname)) {
            const redirectUrl = new URLSearchParams(window.location.search).get("redirect_url");
            navigate(redirectUrl || DEFAULT_PAGE);
          }
        }
        dispatch(fetchAiCredits());
      })
      .catch((_error) => {
        setTimeout(() => {
          dispatch(updatePageLoading(false));
        }, 30);
        dispatch(updateUserInfo(null));
        dispatch(
          updateCreditInfo({
            monthlyRemain: 0,
            permanentRemain: 0,
            totalRemain: 0,
            subAccount: false,
            mainTotalRemain: 0,
          }),
        );
      });
  }, []);

  useEffect(() => {
    dispatch(fetchGlobalConfig());
  }, [language]);

  // 监听事件冒泡
  useEffect(() => {
    const fn = (e: any) => {
      let { kalo_pos, kalo_value, kalo_navigate } = e?.target?.dataset || {};
      if (kalo_navigate) kalo_value = kalo_navigate;
      if (kalo_pos) {
        // 有navigate 先上报 再切换路由
        report.click({ kalo_pos, kalo_value });
        if (kalo_navigate) navigate(kalo_navigate);
      }
      if (kalo_navigate) navigate(kalo_navigate);
    };
    window.addEventListener("click", fn);
    return () => {
      window.removeEventListener("click", fn);
    };
  }, []);

  useEffect(() => {
    return () => {
      const endPageTime = new Date().getTime();
      if (configReady) {
        report.event("Staytime", {
          kalo_value: endPageTime - startPageTime,
        });
      }
    };
  }, [configReady, location.pathname]);

  useEffect(() => {
    const pre_pathname = sessionStorage.getItem("cur_pathname") || document.referrer || "NULL";
    const cur_pathname = location.pathname;
    sessionStorage.setItem("pre_pathname", pre_pathname);
    const isFirstEntry = sessionStorage.getItem("cur_pathname") === null;
    sessionStorage.setItem("cur_pathname", cur_pathname);
    let pointParams = {
      pre_pathname: pre_pathname,
      kalo_pathname: cur_pathname,
      kalo_type: isFirstEntry ? "first_entry" : "not_first_entry",
      kalo_value: pre_pathname === cur_pathname ? "refresh" : "",
    };
    report.event("kalo_pageview", pointParams);
  }, [location.pathname]);

  useEffect(() => {
    if (window.history) {
      window.onpopstate = function () {
        report.event("history_popstate");
      };
    }
  }, []);
  useEffect(() => {
    if (import.meta.env.MODE === "production") {
      try {
        __bl.setConfig({
          ignore: {
            ignoreApis: [/.+\/api\/log$/, /execute-api/, /googlesyndication/, /clarity/],
            ignoreErrors: [/MutationRecord/, /status code/, /Network Error/, /clarity/],
          },
        });
      } catch (err) {}
    }
  }, []);

  useEffect(() => {
    if (window.innerHeight <= 720) {
      dispatch(updateIsScreenlt720(true));
    }
  }, []);

  useWindowResize({
    callback: () => {
      if (window.innerHeight <= 720) {
        dispatch(updateIsScreenlt720(true));
      } else {
        dispatch(updateIsScreenlt720(false));
      }
    },
  });

  useEffect(() => {
    PubSub.subscribe("LoginStatus1302", (_, data: boolean) => {
      setVisibleRemainderCountModal(data);
    });
    return () => {
      PubSub.unsubscribe("LoginStatus1302");
    };
  }, []);

  let newLoginLayout = isNewLoginConfig({ language });
  const isPrivacyPolicyPage = location.pathname === "/privacy-policy";

  return (
    <div
      className={classNames("app", {
        app_new_layout: newLoginLayout,
        app_privacy_policy: isPrivacyPolicyPage,
      })}
    >
      <RenderPage />
      <div className="app_header_wrapper"></div>
      <div className="app_login_wrapper">
        <div className="page">
          <LoginRouter></LoginRouter>
        </div>
        <GlobalLayout></GlobalLayout>
      </div>
      <SpriteUrl className="absolute z-[-1] top-[-10000px]" />
    </div>
  );
};

export default App;
